import React from 'react';
import SectionHeader from './SectionHeader';
import TextInput from './TextInput';
import ChoiceRadio from './ChoiceRadio';
import NotesInput from './NotesInput';
import { COMMUNICATION_FREQUENCIES } from './options';
import { SECTION_DEFAULTS, useSectionDefaults } from './defaults';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const PAPERLESS_OPTIONS = [
  { value: 'yes', label: 'Yes.' },
  { value: 'no', label: 'No, I want to opt out of paperless.' },
];

const QUARTERLY_OPTIONS = [
  { value: 'yes', label: 'Yes.' },
  { value: 'no', label: 'No, I want my reports by postal mail.' },
];

/**
 * Compliance and reporting questions.
 *
 * The two affiliation questions default to No and the two delivery questions
 * default to Yes, matching the paper form. When either affiliation answer is
 * Yes we collect the follow-up detail the custodian's application requires.
 *
 * The contact-frequency question deliberately has NO default — it is a real
 * preference we are asking for, not a form we are pre-filling.
 */
export default function StepCompliance({ number, person = 'primary', data, onChange }) {
  useSectionDefaults(data, onChange, SECTION_DEFAULTS.compliance);

  const d = data || {};
  const isSpouse = person === 'spouse';
  const p = isSpouse ? 'spouse_compliance' : 'compliance';
  const who = isSpouse ? "Your Spouse/Partner's" : 'Your';
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  return (
    <div>
      <SectionHeader
        number={number}
        title={`${who} Compliance & Reporting`}
        tip="These questions are required by our custodians. Most clients answer No to the first two."
      />

      <ChoiceRadio
        id={`${p}-exchange`}
        inline
        label={
          <>
            Are you affiliated with or employed by a stock exchange, a member
            of an exchange or FINRA, or a municipal securities broker-dealer?{' '}
            <span className="text-danger">*</span>
          </>
        }
        options={YES_NO}
        value={d.exchange_affiliated}
        onChange={update('exchange_affiliated')}
      />
      {d.exchange_affiliated === 'yes' && (
        <div className="row g-3 mb-3">
          <TextInput
            id={`${p}-exchange-details`}
            label="Firm name and your relationship to it"
            col="col-md-8"
            required
            value={d.exchange_affiliated_details}
            onChange={update('exchange_affiliated_details')}
            autoComplete="off"
          />
        </div>
      )}

      <ChoiceRadio
        id={`${p}-insider`}
        inline
        label={
          <>
            Are you a director, 10 percent shareholder, or policy-making
            officer of a publicly held company?{' '}
            <span className="text-danger">*</span>
          </>
        }
        options={YES_NO}
        value={d.public_company_insider}
        onChange={update('public_company_insider')}
      />
      {d.public_company_insider === 'yes' && (
        <div className="row g-3 mb-3">
          <TextInput
            id={`${p}-insider-details`}
            label="Company name and ticker symbol"
            col="col-md-8"
            required
            value={d.public_company_insider_details}
            onChange={update('public_company_insider_details')}
            autoComplete="off"
          />
        </div>
      )}

      <hr className="my-4" />

      <ChoiceRadio
        id={`${p}-paperless`}
        label={<>Paperless document delivery <span className="text-danger">*</span></>}
        help="You will receive account statements, trade confirmations, shareholder materials, account agreements and disclosures, and other regulatory documents (when available) electronically by client portal instead of paper. Is this okay?"
        options={PAPERLESS_OPTIONS}
        value={d.paperless_delivery}
        onChange={update('paperless_delivery')}
      />

      <ChoiceRadio
        id={`${p}-quarterly`}
        label={<>Quarterly reports <span className="text-danger">*</span></>}
        help="You will receive Halter Ferguson Financial's Quarterly Reports via client portal. Is this okay?"
        options={QUARTERLY_OPTIONS}
        value={d.quarterly_reports_portal}
        onChange={update('quarterly_reports_portal')}
      />

      <ChoiceRadio
        id={`${p}-communication-frequency`}
        label={
          <>
            In addition to one-on-one calls and emails specific to your
            accounts and situation, and your quarterly reports, how often would
            you like to hear from us? <span className="text-danger">*</span>
          </>
        }
        options={COMMUNICATION_FREQUENCIES}
        value={d.communication_frequency}
        onChange={update('communication_frequency')}
      />

      <NotesInput
        value={d.notes}
        onChange={update('notes')}
        label="Anything else we should know about this section?"
        placeholder="Optional"
      />
    </div>
  );
}
